import { feature, featureCollection } from "@turf/turf";
import { Feature, Point } from "geojson";
import { ReactNode, useMemo, useState } from "react";

import { VISIT_STATUS_TO_COLOR_MAP } from "~/config/statusColors";
import GaiaMapDataLayer from "~/modules/map/components/GaiaMapDataLayer";
import GaiaMapFeatureClickListener from "~/modules/map/components/GaiaMapEventListeners/GaiaMapFeatureClickListener";
import { GaiaMapFeatureHoverListener } from "~/modules/map/components/GaiaMapEventListeners/GaiaMapFeatureHoverListener";
import { GaiaMapMarker } from "~/modules/map/components/GaiaMapMarker";
import GaiaMapStyle from "~/modules/map/components/GaiaMapStyle";
import { LAYER_DEFAULTS } from "~/modules/map/components/GaiaMapStyleComposites/layerDefaults";
import { IMG_MARKER_CHANGE_CIRCLE_OUTLINED } from "~/modules/map/mapStyles";
import { GaiaMapMarkerArea } from "~/modules/plannerV2/components/GaiaMapMarkerArea";

type AreaLayerV2AreaBase = {
    id: string;
    location: Point;
    plannerStatusBucket: string;
    hasStagedChanges: boolean;
    earliestPlannerVisitDaysRemaining?: number | null;
};

type AreasLayerV2Props<T> = {
    areas: T[];
    areasById?: Map<string, T>;
    selectedArea?: T;
    onAreaClick?: (areaId: string) => void;
    renderHoveredMarker?: (area: T, onMouseLeave: () => void) => ReactNode;
    renderSelectedMarker?: (area: T) => ReactNode;
    layerId?: string; // todo figure out how to do this better
};

const AREA_SOURCE_ID = "planner_areas_source";
const AREA_LAYER_ID = "planner_areas_layer";
const AREA_PENDING_ICON_LAYER_ID = "planner_panding_changes_areas_layer";
const AREA_DAYS_REMAINING_LAYER_ID = "planner_areas_days_remaining_layer";

export const AREA_MARKER_OFFSET_HOVER: [number, number] = [-28, -28];
export const AREA_MARKER_OFFSET_HOVER_EXPANDED: [number, number] = [-32, -32];
export const AREA_MARKER_OFFSET_SELECTED: [number, number] = [-32, -32];

function useAreasGeoJson(areas: AreaLayerV2AreaBase[]) {
    return useMemo(() => {
        return featureCollection(
            areas.map(area =>
                feature(area.location, {
                    ...area,
                }),
            ),
        );
    }, [areas]);
}

export function AreasLayerV2<T extends AreaLayerV2AreaBase>({
    areas,
    areasById,
    onAreaClick,
    selectedArea,
    renderHoveredMarker,
    layerId = "",
}: AreasLayerV2Props<T>) {
    const [hoveredArea, setHoveredArea] = useState<T | undefined>(undefined);
    const areasGeoJson = useAreasGeoJson(areas);

    return (
        <>
            <GaiaMapDataLayer id={`${AREA_SOURCE_ID}-${layerId}`} data={areasGeoJson} />

            <GaiaMapStyle
                layers={[
                    {
                        id: `${AREA_LAYER_ID}-${layerId}`,
                        source: `${AREA_SOURCE_ID}-${layerId}`,
                        type: "circle",
                        slot: "top",
                        paint: {
                            "circle-color": "white",
                            "circle-radius": 15,
                            "circle-stroke-color": [
                                "match",
                                ["get", "plannerStatusBucket"],
                                ...Object.keys(VISIT_STATUS_TO_COLOR_MAP).flatMap(status => [
                                    status,
                                    VISIT_STATUS_TO_COLOR_MAP[status],
                                ]),
                                "#000", // fallback
                            ],
                            "circle-stroke-width": 8,
                        },
                    },
                    {
                        id: `${AREA_PENDING_ICON_LAYER_ID}-${layerId}`,
                        source: `${AREA_SOURCE_ID}-${layerId}`,
                        type: "symbol",
                        slot: "top",
                        layout: {
                            ...LAYER_DEFAULTS.symbolLayout,
                            "icon-image": IMG_MARKER_CHANGE_CIRCLE_OUTLINED,
                            "icon-size": 0.3,
                        },
                        paint: {
                            "icon-color": LAYER_DEFAULTS.iconColor.onLight,
                        },
                        filter: ["==", ["get", "hasStagedChanges"], true],
                    },
                    {
                        id: `${AREA_DAYS_REMAINING_LAYER_ID}-${layerId}`,
                        source: `${AREA_SOURCE_ID}-${layerId}`,
                        type: "symbol",
                        slot: "top",
                        minzoom: 11,
                        layout: {
                            ...LAYER_DEFAULTS.symbolLayout,
                            "text-field": ["to-string", ["get", "earliestPlannerVisitDaysRemaining"]],
                            "text-font": [...LAYER_DEFAULTS.textLayout["text-font"]],
                            "text-size": 12,
                        },
                        paint: {
                            "text-color": "#000000",
                        },
                        filter: [
                            "all",
                            ["in", ["get", "plannerStatusBucket"], ["literal", ["plannableOk", "plannableAtRisk"]]],
                            ["==", ["typeof", ["get", "earliestPlannerVisitDaysRemaining"]], "number"],
                            ["==", ["get", "hasStagedChanges"], false],
                        ],
                    },
                ]}
            />

            <GaiaMapFeatureHoverListener<Feature<Point, T>>
                onHoverCallback={feature => {
                    if (!feature || !areasById) return;

                    const areaId = feature.properties.id;
                    if (areaId === hoveredArea?.id || areaId === selectedArea?.id) {
                        return;
                    }

                    const fullAreasLayerV2Fragment = areasById.get(areaId);
                    if (!fullAreasLayerV2Fragment) return;

                    setHoveredArea(fullAreasLayerV2Fragment);
                }}
                layerIds={[`${AREA_LAYER_ID}-${layerId}`]}
            />

            <GaiaMapFeatureClickListener
                onClick={feature => {
                    if (!feature?.properties?.id || feature?.properties?.id === selectedArea?.id) return;
                    onAreaClick?.(feature.properties.id);
                }}
                layerIds={[`${AREA_LAYER_ID}-${layerId}`]}
            />

            {hoveredArea &&
                (renderHoveredMarker ? (
                    renderHoveredMarker(hoveredArea, () => setHoveredArea(undefined))
                ) : (
                    <GaiaMapMarker
                        coordinates={hoveredArea.location.coordinates}
                        onMouseLeave={() => setHoveredArea(undefined)}
                        offset={AREA_MARKER_OFFSET_HOVER}
                    >
                        <GaiaMapMarkerArea
                            plannerStatusBucket={hoveredArea.plannerStatusBucket}
                            hasStagedChanges={hoveredArea.hasStagedChanges}
                        />
                    </GaiaMapMarker>
                ))}

            {selectedArea && (
                <GaiaMapMarker coordinates={selectedArea.location.coordinates} offset={AREA_MARKER_OFFSET_SELECTED}>
                    <GaiaMapMarkerArea
                        selected
                        plannerStatusBucket={selectedArea.plannerStatusBucket}
                        hasStagedChanges={selectedArea.hasStagedChanges}
                    />
                </GaiaMapMarker>
            )}
        </>
    );
}
